import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { noop } from "../redux/api";
import { selectServerGroup } from "../redux/reducer/app-slice";
import { countUnseen, markSeen, readSeen, writeSeen } from "../utils/app-badge";
import {
  closeGroupNotifications,
  pendingCount,
} from "../utils/push-notification";
import { getPushRegistration } from "../utils/push-subscription";

const storage = () => {
  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
};

const isSupported = () =>
  typeof navigator !== "undefined" && "setAppBadge" in navigator;

const setBadge = (count) => {
  const call =
    count > 0 ? navigator.setAppBadge(count) : navigator.clearAppBadge?.();
  Promise.resolve(call).catch(noop);
};

const withRegistration = (use) =>
  getPushRegistration()
    .then((registration) => registration && use(registration))
    .catch(noop);

const notificationCount = () =>
  withRegistration((registration) =>
    registration.getNotifications().then(pendingCount),
  ).then((count) => count || 0);

const syncBadge = (group) => {
  if (document.visibilityState === "visible") {
    if (group?.id) {
      writeSeen(storage(), markSeen(readSeen(storage()), group));
      withRegistration((registration) =>
        closeGroupNotifications(registration, group.id),
      );
    }
    setBadge(0);
    return;
  }
  if (!group?.id) return;
  const unseen = countUnseen(readSeen(storage()), group);
  notificationCount().then((pushed) => setBadge(Math.max(unseen, pushed)));
};

export const useAppBadge = () => {
  const group = useSelector(selectServerGroup);
  const latest = useRef(group);
  latest.current = group;

  useEffect(() => {
    if (isSupported()) syncBadge(group);
  }, [group]);

  useEffect(() => {
    if (!isSupported()) return undefined;
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") syncBadge(latest.current);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);
};
